import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Plus, Edit, Trash2, Loader2 } from "lucide-react";

const ModelCard = ({ model, onEdit, onDelete }) => (
  <Card className="border-neutral-100 overflow-hidden">
    <img src={model.preview_image_url} alt={model.name} className="w-full aspect-[3/4] object-cover" />
    <CardContent className="p-4">
      <h3 className="font-semibold text-neutral-900">{model.name}</h3>
      <p className="text-sm text-neutral-500 capitalize mt-1">{model.gender} • {model.age_group} • {model.complexion}</p>
      <div className="flex gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={() => onEdit(model)}>
          <Edit className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={() => onDelete(model.model_id)} className="text-red-600 hover:text-red-700">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

const AdminModels = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "", gender: "female", age_group: "18-25", complexion: "fair",
    hair: "long", body_type: "slim", ethnicity_style: "indian", preview_image_url: ""
  });

  useEffect(() => { fetchModels(); }, []);

  const fetchModels = async () => {
    try {
      const response = await axios.get(`${API}/models`, { withCredentials: true });
      setModels(response.data);
    } catch (error) {
      toast.error("Failed to load models");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "", gender: "female", age_group: "18-25", complexion: "fair",
      hair: "long", body_type: "slim", ethnicity_style: "indian", preview_image_url: ""
    });
    setEditingModel(null);
  };

  const handleEdit = (model) => {
    setEditingModel(model);
    setFormData({
      name: model.name, gender: model.gender, age_group: model.age_group, complexion: model.complexion,
      hair: model.hair, body_type: model.body_type, ethnicity_style: model.ethnicity_style, preview_image_url: model.preview_image_url
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.preview_image_url) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      if (editingModel) {
        await axios.put(`${API}/admin/models/${editingModel.model_id}`, formData, { withCredentials: true });
        toast.success("Model updated successfully");
      } else {
        await axios.post(`${API}/admin/models`, formData, { withCredentials: true });
        toast.success("Model created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchModels();
    } catch (error) {
      toast.error("Failed to save model");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (modelId) => {
    if (!window.confirm("Are you sure you want to disable this model?")) return;
    try {
      await axios.delete(`${API}/admin/models/${modelId}`, { withCredentials: true });
      toast.success("Model disabled");
      fetchModels();
    } catch (error) {
      toast.error("Failed to disable model");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="admin-models-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-neutral-900">AI Models</h1>
            <p className="text-neutral-600 mt-1">Manage AI models available for photoshoots</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-700 hover:bg-indigo-800 rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Model
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingModel ? "Edit Model" : "Add New Model"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Name</Label>
                  <Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="Model name" className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Gender</Label>
                    <select className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg" value={formData.gender} onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                      <option value="kid">Kid</option>
                    </select>
                  </div>
                  <div>
                    <Label>Age Group</Label>
                    <select className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg" value={formData.age_group} onChange={(e) => setFormData(prev => ({ ...prev, age_group: e.target.value }))}>
                      <option value="kids">Kids</option>
                      <option value="18-25">18-25</option>
                      <option value="26-35">26-35</option>
                      <option value="36-45">36-45</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Complexion</Label>
                    <select className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg" value={formData.complexion} onChange={(e) => setFormData(prev => ({ ...prev, complexion: e.target.value }))}>
                      <option value="fair">Fair</option>
                      <option value="wheatish">Wheatish</option>
                      <option value="dusky">Dusky</option>
                      <option value="dark">Dark</option>
                    </select>
                  </div>
                  <div>
                    <Label>Hair</Label>
                    <select className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg" value={formData.hair} onChange={(e) => setFormData(prev => ({ ...prev, hair: e.target.value }))}>
                      <option value="short">Short</option>
                      <option value="long">Long</option>
                      <option value="curly">Curly</option>
                      <option value="straight">Straight</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Body Type</Label>
                    <select className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg" value={formData.body_type} onChange={(e) => setFormData(prev => ({ ...prev, body_type: e.target.value }))}>
                      <option value="slim">Slim</option>
                      <option value="athletic">Athletic</option>
                      <option value="curvy">Curvy</option>
                    </select>
                  </div>
                  <div>
                    <Label>Ethnicity Style</Label>
                    <select className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg" value={formData.ethnicity_style} onChange={(e) => setFormData(prev => ({ ...prev, ethnicity_style: e.target.value }))}>
                      <option value="indian">Indian</option>
                      <option value="western">Western</option>
                      <option value="royal">Royal</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Preview Image URL</Label>
                  <Input value={formData.preview_image_url} onChange={(e) => setFormData(prev => ({ ...prev, preview_image_url: e.target.value }))} placeholder="https://..." className="mt-1" />
                </div>
                <Button onClick={handleSave} disabled={saving} className="w-full bg-indigo-600 hover:bg-indigo-700">
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingModel ? "Update Model" : "Create Model"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
          {models.map((model) => (
            <ModelCard key={model.model_id} model={model} onEdit={handleEdit} onDelete={handleDelete} />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminModels;
